import { Link, useParams } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHead, CtaBand, Breadcrumbs, Faq, LinkGrid } from "@/components/Shared";
import { PROVINCES, CITIES, SERVICES, BRAND, CITY_COUNT_LABEL } from "@/data/site";
import { url, metaLocations, metaProvince, metaCity, provBySlug, cityBySlug, citiesInProv, cityContent, provinceContent, breadcrumbLd, faqLd } from "@/data/content";
import NotFound from "@/pages/NotFound";

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Shield;
  return <C className={className} />;
};

export function LocationsIndex() {
  const meta = metaLocations();
  return (
    <div data-testid="locations-index-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
      <Seo title={meta.title} description={meta.description} path="/locations/" jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations/" }])]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Locations", path: "/locations/" }]} />
      <SectionHead as="h1" eyebrow="Security Guard Service Areas" title={`Every province. Every territory. ${CITY_COUNT_LABEL} cities.`} desc={`${BRAND.name} deploys licensed security guards across Canada. Select your province or city for local coverage details, services and dispatch information.`} />
      <div className="space-y-10" data-testid="provinces-selector">
        {PROVINCES.map((p) => {
          const cities = citiesInProv(p.slug);
          return (
            <section key={p.slug} className="rounded-2xl border border-slate-800 bg-[#111827] p-7" data-testid={`province-section-${p.slug}`}>
              <Link to={url.prov(p)} className="group flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold font-display text-slate-50 group-hover:text-amber-400 transition-colors">
                  Security Guard Services in {p.name} <span className="font-mono text-xs text-amber-500/80 ml-2">{p.abbr}</span>
                </h2>
                <Lucide.ArrowRight className="h-5 w-5 text-amber-500 transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="text-sm text-slate-400 mb-5">{p.blurb}</p>
              <div className="flex flex-wrap gap-2">
                {cities.map((c) => (
                  <Link key={c.slug} to={url.city(c)} data-testid={`city-link-${c.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3.5 py-1.5 text-xs text-slate-300 transition-all hover:border-amber-500/60 hover:text-amber-400">
                    <Lucide.MapPin className="h-3 w-3 text-amber-500/70" /> {c.name}
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <CtaBand heading="Your City Not Listed?" sub="We deploy nationally, including remote and northern communities on rotation. Call dispatch and we will confirm coverage for your location." />
    </div>
  );
}

export function ProvincePage() {
  const { provSlug } = useParams();
  const p = provBySlug(provSlug);
  if (!p) return <NotFound />;
  const meta = metaProvince(p);
  const pc = provinceContent(p);
  const cities = citiesInProv(p.slug);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Locations", path: "/locations/" }, { name: p.name, path: url.prov(p) }];
  return (
    <div data-testid={`province-page-${p.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.prov(p)} jsonLd={[faqLd(pc.faqs), breadcrumbLd(crumbs)]} />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 relative">
          <Breadcrumbs items={crumbs} />
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-amber-500 mb-4">Licensed Security Guards — {p.abbr}</span>
          <h1 data-testid="province-h1" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-4xl">{pc.h1}</h1>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={BRAND.phoneHref} data-testid="province-call-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
              <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.emailHref} data-testid="province-email-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
              <Lucide.Mail className="h-4 w-4" /> {BRAND.email}
            </a>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed mb-12">
          {pc.intro.map((para, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{para}</p>)}
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mb-6">Cities We Serve in {p.name}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
          {cities.map((c) => (
            <Link key={c.slug} to={url.city(c)} data-testid="city-location-card" className="group rounded-xl border border-slate-800 bg-[#111827] p-5 transition-all hover:border-amber-500/50 hover:-translate-y-1">
              <div className="flex items-center gap-2 mb-2">
                <Lucide.MapPin className="h-4 w-4 text-amber-400" />
                <h3 className="font-display font-bold text-slate-100 group-hover:text-amber-400 transition-colors">{c.name}</h3>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">Security guard services across {c.metro}.</p>
            </Link>
          ))}
        </div>
        <LinkGrid
          testid="province-services"
          title={`Security Services Available Across ${p.name}`}
          links={SERVICES.map((s) => ({ path: url.serviceProv(s, p), label: `${s.name} ${p.name}` }))}
        />
        <Faq items={pc.faqs} heading={`Security Guard Services in ${p.name} — FAQs`} />
        <CtaBand heading={`Get Security Guards Anywhere in ${p.name}`} sub={`One dispatch desk for the entire province. Call ${BRAND.phone} or email ${BRAND.email} for a same-day quote.`} />
      </div>
    </div>
  );
}

export function CityPage() {
  const { provSlug, citySlug } = useParams();
  const p = provBySlug(provSlug);
  const c = cityBySlug(citySlug);
  if (!p || !c || c.prov !== p.slug) return <NotFound />;
  const meta = metaCity(c);
  const cc = cityContent(c);
  const nearby = citiesInProv(p.slug).filter((x) => x.slug !== c.slug);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Locations", path: "/locations/" }, { name: p.name, path: url.prov(p) }, { name: c.name, path: url.city(c) }];
  return (
    <div data-testid={`city-page-${c.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.city(c)} jsonLd={[faqLd(cc.faqs), breadcrumbLd(crumbs)]} />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 relative">
          <Breadcrumbs items={crumbs} />
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-amber-500 mb-4">24/7 Security Company — {c.name}, {p.abbr}</span>
          <h1 data-testid="city-h1" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-4xl">{cc.h1}</h1>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={BRAND.phoneHref} data-testid="city-call-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
              <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.emailHref} data-testid="city-email-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
              <Lucide.Mail className="h-4 w-4" /> {BRAND.email}
            </a>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
              {cc.intro.map((para, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{para}</p>)}
            </div>
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              <div className="rounded-xl border border-slate-800 bg-[#111827] p-6">
                <h2 className="font-display font-bold text-slate-50 mb-4 flex items-center gap-2"><Lucide.Landmark className="h-4 w-4 text-amber-400" /> Areas We Cover in {c.name}</h2>
                <ul className="space-y-2.5">
                  {c.landmarks.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm text-slate-400"><Lucide.CheckCircle2 className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> {l.charAt(0).toUpperCase() + l.slice(1)}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-slate-800 bg-[#111827] p-6">
                <h2 className="font-display font-bold text-slate-50 mb-4 flex items-center gap-2"><Lucide.Briefcase className="h-4 w-4 text-amber-400" /> Key {c.name} Sectors Protected</h2>
                <ul className="space-y-2.5">
                  {c.industries.map((ind) => (
                    <li key={ind} className="flex items-start gap-2 text-sm text-slate-400"><Lucide.CheckCircle2 className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> {ind.charAt(0).toUpperCase() + ind.slice(1)}</li>
                  ))}
                </ul>
              </div>
            </div>
            <Faq items={cc.faqs} heading={`Security Guard Services in ${c.name} — FAQs`} />
          </div>
          <aside>
            <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#111827] to-slate-900 p-6 sticky top-28">
              <Lucide.ShieldCheck className="h-8 w-8 text-amber-400 mb-4" />
              <h3 className="font-display font-bold text-slate-50 mb-2">{c.name} Security Dispatch</h3>
              <p className="text-xs text-slate-400 leading-relaxed mb-5">Emergency guard deployment available tonight across {c.metro}.</p>
              <a href={BRAND.phoneHref} data-testid="city-sidebar-call" className="flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors mb-3">
                <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} data-testid="city-sidebar-email" className="flex items-center justify-center gap-2 rounded-full border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
                <Lucide.Mail className="h-4 w-4" /> Email Dispatch
              </a>
            </div>
          </aside>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mb-6">All Security Services in {c.name}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14" data-testid="city-services-grid">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={url.serviceCity(s, c)} data-testid={`city-service-${s.slug}`} className="group rounded-xl border border-slate-800 bg-[#111827] p-5 transition-all hover:border-amber-500/50 hover:-translate-y-1">
              <div className="flex items-center gap-3 mb-2">
                <Icon name={s.icon} className="h-5 w-5 text-amber-400" />
                <h3 className="font-semibold text-slate-100 text-sm group-hover:text-amber-400 transition-colors">{s.name} {c.name}</h3>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{s.short}</p>
            </Link>
          ))}
        </div>

        {nearby.length > 0 && (
          <LinkGrid
            testid="nearby-cities"
            title={`Security Services in Other ${p.name} Cities`}
            links={nearby.map((x) => ({ path: url.city(x), label: `Security Guards ${x.name}` }))}
          />
        )}
        <CtaBand heading={`Hire Security Guards in ${c.name} Today`} sub={`Licensed, insured officers across ${c.metro}. Call ${BRAND.phone} or email ${BRAND.email} for a same-day written quote.`} />
      </div>
    </div>
  );
}
